<script lang="ts">
  import {
    type SeTenants,
    type ReviewState,
    type WithoutIdAndReview,
    canReview,
    canEdit,
  } from '@briefmarken_katalog/types';

  import DateOfIssue from './DateOfIssue.svelte';
  import Title from './Title.svelte';
  import FieldSeparator from './FieldSeparator.svelte';
  import Picture from '../Picture.svelte';
  import SeTenantsEditor from '../editors/SeTenantsEditor.svelte';
  import ReviewStateIcon from './ReviewStateIcon.svelte';
  import RowActionButtons from './RowActionButtons.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';

  import { user, doLogin } from '$lib/stores/auth';
  import { catalogMode } from '$lib/stores/ui';
  import { reviewSuggestion, getItem, getSuggestion, deleteSuggestion } from '$lib/services/philatelic_items';
  import { SeTenantsEditorModel } from '../editors/models/SeTenantsEditorModel.svelte';
  import { t } from '$lib/i18n';
  import { applyReviewResult } from './applyReviewResult';

  import './row.css';

  const { seTenants, readonly = false }: {
    seTenants: SeTenants;
    readonly?: boolean;
  } = $props<{ seTenants: SeTenants; readonly?: boolean }>();

  let isEditorOpen = $state(false);
  let editorModel = $state<SeTenantsEditorModel | null>(null);
  let catalogDetailItem = $state<SeTenants | null>(null);
  let isCatalogDetailOpen = $state(false);

  async function fetchFull(): Promise<SeTenants | null> {
    const fetch = seTenants.review.state === 'Accepted' ? getItem : getSuggestion;
    return await fetch(seTenants._id!, doLogin) as SeTenants | null;
  }

  async function markAsInReview() {
    try {
      applyReviewResult(seTenants, await reviewSuggestion(seTenants._id!, 'InReview', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function markAsSuggested() {
    try {
      applyReviewResult(seTenants, await reviewSuggestion(seTenants._id!, 'Suggested', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  function suggestedBySuffix(full: SeTenants): string {
    return full.review.suggestedByName
      ? ` ${$t('editor.suggestedBy')} ${full.review.suggestedByName}`
      : '';
  }

  async function openReview() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new SeTenantsEditorModel({
      isReview: true,
      initialValue: full,
      dialogTitle: 'editor.seTenantsReviewTitle',
      dialogSubtitle: 'editor.seTenantsReviewSubtitle',
      onCreate: async (updatedSeTenants: WithoutIdAndReview<SeTenants>) => {
        try {
          applyReviewResult(seTenants, await reviewSuggestion(seTenants._id!, seTenants.review.state, updatedSeTenants, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
      onReview: async (updatedSeTenants: WithoutIdAndReview<SeTenants>, state: ReviewState) => {
        try {
          applyReviewResult(seTenants, await reviewSuggestion(seTenants._id!, state, updatedSeTenants, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDelete() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new SeTenantsEditorModel({
      isDelete: true,
      initialValue: full,
      dialogTitle: 'deleteSuggestion.title',
      dialogSubtitle: 'deleteSuggestion.subtitle',
      onDelete: async () => {
        try {
          await deleteSuggestion(seTenants._id!, doLogin);
        } catch (e: any) {
          alert($t('errors.deleteSuggestion') + e.message);
        }
      },
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDetails(ro: boolean) {
    const full = await fetchFull();
    if (!full) return;
    // Catalog mode gets the presentation-style detail dialog instead of the editor
    if (ro && $catalogMode) {
      catalogDetailItem = full;
      isCatalogDetailOpen = true;
      return;
    }
    const subtitleKey = seTenants.review.state !== 'Accepted'
      ? 'editor.seTenantsDetailsSuggestionSubtitle' as const
      : 'editor.seTenantsDetailsCatalogSubtitle' as const;
    editorModel = new SeTenantsEditorModel({
      isReadonly: ro,
      isEdit: !ro,
      initialValue: full,
      dialogTitle: 'editor.seTenantsDetailsTitle',
      dialogSubtitle: subtitleKey,
      ...(!ro && {
        onCreate: async (updatedSeTenants: WithoutIdAndReview<SeTenants>) => {
          try {
            applyReviewResult(seTenants, await reviewSuggestion(seTenants._id!, seTenants.review.state, updatedSeTenants, doLogin));
          } catch (e: any) {
            alert($t('errors.reviewFailed') + e.message);
          }
        },
      }),
    });
    if (seTenants.review.state !== 'Accepted') editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }
</script>

<!-- Desktop: single flex line (wrappers dissolve via sm:contents / sm:order-*); mobile: stacked lines -->
<div class="row max-sm:flex-col max-sm:gap-1">
  <!-- mobile line 1: picture + title -->
  <div class="max-sm:flex max-sm:items-start max-sm:gap-1 max-sm:w-full sm:contents">
    <div class="shrink-0 sm:order-3">
      <Picture
        picture={seTenants.pictures ? seTenants.pictures[0] : undefined}
        fetchFullImage={() => fetchFull().then(f => f?.pictures?.[0])}
      />
    </div>
    <div class="min-w-0 flex-1 sm:order-5">
      <Title text={seTenants.title}/>
    </div>
  </div>
  <!-- mobile line 2: date · review state · actions -->
  <div class="max-sm:flex max-sm:items-center max-sm:w-full sm:contents">
    <div class="sm:order-1">
      <DateOfIssue date={seTenants.date_of_issue}/>
    </div>
    {#if !readonly}
      <div class="sm:order-6">
        <ReviewStateIcon
          state={seTenants.review.state}
          reviewedBy={seTenants.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          onMarkAsInReview={markAsInReview}
          onDropReview={markAsSuggested}
        />
      </div>
      <div class="max-sm:ml-auto sm:order-8">
        <RowActionButtons
          state={seTenants.review.state}
          reviewedBy={seTenants.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          userCanEdit={!!$user && canEdit(seTenants, $user.id)}
          userIsAdmin={$user?.role === 'Admin'}
          userIsLoggedIn={!!$user}
          onReview={openReview}
          onDelete={openDelete}
          onDetails={openDetails}
        />
      </div>
    {:else}
      <div class="max-sm:ml-auto sm:order-8">
        <RowActionButtons
          readonly={true}
          state={seTenants.review.state}
          reviewedBy={seTenants.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          userCanEdit={false}
          userIsAdmin={false}
          userIsLoggedIn={!!$user}
          onReview={openReview}
          onDetails={openDetails}
        />
      </div>
    {/if}
  </div>
  <!-- desktop-only vertical dividers -->
  <FieldSeparator class="sm:order-2"/>
  <FieldSeparator class="sm:order-4"/>
  {#if !readonly}
    <FieldSeparator class="sm:order-7"/>
  {/if}
</div>

{#if isEditorOpen && editorModel}
  <SeTenantsEditor bind:model={editorModel!} bind:isOpen={isEditorOpen}/>
{/if}

{#if isCatalogDetailOpen && catalogDetailItem}
  <CatalogDetailDialog item={catalogDetailItem} bind:isOpen={isCatalogDetailOpen} />
{/if}
